import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import { SERVER_BASE } from '../config/server';
import '../styles/addCenter.css';
import apiClient from '../utils/apiClient';
import Swal from 'sweetalert2';
import imageCompression from 'browser-image-compression';

const AddCenter = () => {
    const [activeTab, setActiveTab] = useState('basicInfo');
    const [prefixRows, setPrefixRows] = useState([{ prefix: '', startingNumber: '' }]);
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [formData, setFormData] = useState({
        centerCode: '',
        centerName: '',
        panNumber: '',
        email: '',
        password: '',
        gstin: '',
        state: '',
        district: '',
        pincode: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        contactPerson1: '',
        position1: '',
        mobile1: '',
        contactPerson2: '',
        position2: '',
        mobile2: '',
        signature: null
    });
    const [inchargeOptions, setInchargeOptions] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();
    const editData = location.state?.centerData || null;
    const isEditMode = Boolean(editData);

    // Fetch incharge dropdown data
    useEffect(() => {
        const token = localStorage.getItem('token');
        apiClient.get(`${SERVER_BASE}/centerIncharge`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(response => {
                const options = response.data.map(incharge => ({
                    value: incharge.id,
                    label: incharge.name,
                    mobile: incharge.mobile
                }));
                setInchargeOptions(options);
            })
            .catch(error => {
                console.error('Error fetching incharges:', error);
            });
    }, []);

    // Populate form when editing
    useEffect(() => {
        if (isEditMode) {
            setFormData(prev => ({
                ...prev,
                ...editData,
                password: '', // don't prefill password
                signature: null // will be uploaded if changed
            }));

            if (editData.prefixes) {
                try {
                    const parsed = typeof editData.prefixes === 'string'
                        ? JSON.parse(editData.prefixes)
                        : editData.prefixes;
                    setPrefixRows(parsed.length ? parsed : [{ prefix: '', startingNumber: '' }]);
                } catch {
                    setPrefixRows([{ prefix: '', startingNumber: '' }]);
                }
            }
            if (editData.signature?.data) {
                const byteArray = new Uint8Array(editData.signature.data);
                let binary = '';
                byteArray.forEach(byte => {
                    binary += String.fromCharCode(byte);
                });
                const base64String = window.btoa(binary);
                setSignaturePreview(`data:image/jpeg;base64,${base64String}`);
            }
        }
    }, [editData, isEditMode]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleContactPerson1Change = (selectedOption) => {
        setFormData({
            ...formData,
            contactPerson1: selectedOption?.label || '',
            mobile1: selectedOption?.mobile || ''
        });
    };

    const handleAddRow = () => {
        setPrefixRows([...prefixRows, { prefix: '', startingNumber: '' }]);
    };

    const handleRowChange = (index, event) => {
        const { name, value } = event.target;
        const updatedRows = [...prefixRows];
        updatedRows[index][name] = value;
        setPrefixRows(updatedRows);
    };

    const handleSignatureChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            Swal.fire('Invalid file type', 'Please select an image file', 'error');
            return;
        }

        try {
            const options = {
                maxSizeMB: 0.2,
                maxWidthOrHeight: 500,
                useWebWorker: true
            };
            const compressedFile = await imageCompression(file, options);

            setFormData({ ...formData, signature: compressedFile });
            setSignaturePreview(null); // Hide existing signature preview

        } catch (error) {
            console.error('Image compression error:', error);
            Swal.fire('Error', 'Failed to compress image', 'error');
        }
    };

    const validateForm = () => {
        const fieldLabels = {
            centerCode: 'Center Code',
            centerName: 'Center Name',
            email: 'Email',
            state: 'State',
            district: 'District',
            pincode: 'Pincode',
            addressLine1: 'Address Line 1',
            password: 'Password'
        };

        const requiredFields = [
            'centerCode', 'centerName', 'email', 'state', 'district', 'pincode', 'addressLine1'
        ];
        if (!isEditMode) {
            requiredFields.push('password');
        }

        for (let field of requiredFields) {
            if (!formData[field]?.trim()) {
                Swal.fire({
                    text: `Please enter ${fieldLabels[field] || field}`,
                    icon: 'error',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
                return false;
            }
        }

        if (!isEditMode && !formData.signature) {
            Swal.fire({
                text: 'Please upload a signature image',
                icon: 'error',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            return false;
        }

        return true;
    };


    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                Swal.fire('Error', 'No token found in localStorage', 'error');
                return;
            }

            const payload = new FormData();
            payload.append('centerCode', formData.centerCode);
            payload.append('centerName', formData.centerName);
            payload.append('panNumber', formData.panNumber);
            payload.append('email', formData.email);
            if (!isEditMode) payload.append('password', formData.password);
            payload.append('gstin', formData.gstin);
            payload.append('state', formData.state);
            payload.append('district', formData.district);
            payload.append('pincode', formData.pincode);
            payload.append('addressLine1', formData.addressLine1);
            payload.append('addressLine2', formData.addressLine2);
            payload.append('addressLine3', formData.addressLine3);
            payload.append('contactPerson1', formData.contactPerson1);
            payload.append('position1', formData.position1);
            payload.append('mobile1', formData.mobile1);
            payload.append('contactPerson2', formData.contactPerson2);
            payload.append('position2', formData.position2);
            payload.append('mobile2', formData.mobile2);

            if (formData.signature) {
                payload.append('signature', formData.signature, formData.signature.name);
            }

            payload.append('prefixRows', JSON.stringify(prefixRows));

            const url = isEditMode
                ? `${SERVER_BASE}/updateCenter/${editData.id}`
                : `${SERVER_BASE}/addCenter`;

            const method = isEditMode ? 'put' : 'post';

            await apiClient[method](url, payload, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                }
            });

            Swal.fire({
                icon: 'success',
                title: isEditMode ? 'Center updated successfully!' : 'Center added successfully!',
                toast: true,
                position: 'top-end',
                timer: 2000,
                showConfirmButton: false,
            }).then(() => {
                navigate('../centerList');
            });

        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire('Error', 'Failed to submit form', 'error');
        }
    };

    const goToNextTab = () => {
        if (activeTab === 'basicInfo') setActiveTab('prefix');
    };

    const goToPreviousTab = () => {
        if (activeTab === 'prefix') setActiveTab('basicInfo');
    };

    return (
        <div className="formcontainer mt-5">
            <div className="d-flex gap-3">
                <button
                    className="btn btn-success mb-3"
                    onClick={() => { navigate('../centerList') }}
                >
                    Back
                </button>
                <h3>{isEditMode ? 'Edit Center' : 'Add Center'}</h3>
            </div>
            <ul className="nav nav-tabs">

                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'basicInfo' ? 'active' : ''}`} onClick={() => setActiveTab('basicInfo')}>Basic Info</button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'prefix' ? 'active' : ''}`} onClick={() => setActiveTab('prefix')}>Prefix</button>
                </li>

            </ul>
            {activeTab != 'prefix' && (
                <div className='d-flex justify-content-end mt-1' style={{ paddingRight: '20px' }}>
                    <button type="button" className="btn btn-success" onClick={handleSubmit}>
                        {isEditMode ? 'Update' : 'Submit'}
                    </button>
                </div>
            )}
            {activeTab == 'basicInfo' && (
                <div className="row p-4">
                    {/* Inputs */}
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="centerCode" placeholder="Enter center code" value={formData.centerCode} onChange={handleChange} required />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="centerName" placeholder="Enter center name" value={formData.centerName} onChange={handleChange} required />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="panNumber" placeholder="Enter PAN number" value={formData.panNumber} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="email" className="form-control" name="email" placeholder="Enter email" value={formData.email} onChange={handleChange} required />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="password" className="form-control" name="password" placeholder="Enter password" value={formData.password} onChange={handleChange} disabled={isEditMode} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="gstin" placeholder="Enter GSTIN" value={formData.gstin} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="state" placeholder="Enter state" value={formData.state} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="district" placeholder="Enter district" value={formData.district} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="pincode" placeholder="Enter pincode" value={formData.pincode} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="addressLine1" placeholder="Enter address line 1" value={formData.addressLine1} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="addressLine2" placeholder="Enter address line 2" value={formData.addressLine2} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="addressLine3" placeholder="Enter address line 3" value={formData.addressLine3} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <Select
                            options={inchargeOptions}
                            onChange={handleContactPerson1Change}
                            placeholder="Select Contact Person 1"
                            value={inchargeOptions.find(opt => opt.label === formData.contactPerson1) || null}
                        />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="mobile1" value={formData.mobile1} readOnly />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="position1" placeholder="Enter position" value={formData.position1} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="contactPerson2" placeholder="Enter contact person 2" value={formData.contactPerson2} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="mobile2" placeholder="Enter mobile number" value={formData.mobile2} onChange={handleChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                        <input type="text" className="form-control" name="position2" placeholder="Enter position" value={formData.position2} onChange={handleChange} />
                    </div>

                    {/* Signature Upload */}
                    <div className="col-md-4 mb-3">
                        <input
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={handleSignatureChange}
                        />
                        {formData.signature && !signaturePreview && (
                            <small className="text-success">Signature uploaded</small>
                        )}

                        {signaturePreview && !formData.signature && (
                            <div className="mt-2">
                                <img
                                    src={signaturePreview}
                                    alt="Signature Preview"
                                    style={{ maxWidth: '100%', height: '80px', border: '1px solid #ccc' }}
                                />
                            </div>
                        )}
                    </div>



                    <div className="d-flex justify-content-end me-2 gap-3">
                        <button type="button" className="btn btn-success" onClick={goToNextTab}>Next</button>


                    </div>
                </div>
            )}

            {activeTab === 'prefix' && (
                <div className="row p-4">
                    {prefixRows.map((row, index) => (
                        <div className="row mb-3" key={index}>
                            <div className="col-md-5">
                                <label>Prefix {index + 1}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="prefix"
                                    value={row.prefix}
                                    onChange={(e) => handleRowChange(index, e)}
                                />
                            </div>
                            <div className="col-md-5">
                                <label>Starting Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="startingNumber"
                                    value={row.startingNumber}
                                    onChange={(e) => handleRowChange(index, e)}
                                />
                            </div>
                            <div className="col-md-2 d-flex align-items-end">
                                <button type="button" className="btn btn-success" onClick={handleAddRow}>
                                    <FontAwesomeIcon icon={faPlusCircle} />
                                </button>
                                {prefixRows.length > 1 && (
                                    <button type="button" className="btn btn-danger ms-2" onClick={() => setPrefixRows(prefixRows.filter((_, i) => i !== index))}>
                                        <FontAwesomeIcon icon={faMinusCircle} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                    <div className="d-flex justify-content-between mt-3">
                        <button type="button" className="btn btn-success" onClick={goToPreviousTab}>Previous</button>
                        <button type="button" className="btn btn-success" onClick={handleSubmit}>
                            {isEditMode ? 'Update' : 'Submit'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddCenter;
